import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Home,
  Search,
  Add,
  AccountCircle,
  Menu as MenuIcon,
  Dashboard,
  Logout
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const userData = user?.user_metadata || {};
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const menuItems = [
    { text: 'Home', path: '/', icon: <Home /> },
    { text: 'Properties', path: '/properties', icon: <Search /> },
  ];

  const authenticatedMenuItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
    { text: 'Add Property', path: '/add-property', icon: <Add /> },
  ];

  const isActive = (path) => location.pathname === path;

  const renderDesktopMenu = () => (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
      {menuItems.map((item) => (
        <Button
          key={item.text}
          component={Link}
          to={item.path}
          color="inherit"
          startIcon={item.icon}
          sx={{
            fontWeight: isActive(item.path) ? 600 : 400,
            borderBottom: isActive(item.path) ? '2px solid white' : 'none',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          {item.text}
        </Button>
      ))}
      
      {isAuthenticated && authenticatedMenuItems.map((item) => (
        <Button
          key={item.text}
          component={Link}
          to={item.path}
          color="inherit"
          startIcon={item.icon}
          sx={{
            fontWeight: isActive(item.path) ? 600 : 400,
            borderBottom: isActive(item.path) ? '2px solid white' : 'none',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          {item.text}
        </Button>
      ))}
    </Box>
  );

  const renderAuthButtons = () => {
    if (isAuthenticated) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            {user?.avatar ? (
              <Avatar src={user.avatar} sx={{ width: 32, height: 32 }} />
            ) : (
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'secondary.main',
                  textTransform: 'uppercase'
                }}
                alt={userData.full_name || 'User'}
                src={userData.avatar_url}
              >
                {(userData.first_name?.charAt(0) || user.email?.charAt(0) || 'U')}
              </Avatar>
            )}
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem 
              onClick={() => { 
                navigate('/profile'); 
                handleMenuClose(); 
              }}
            >
              <AccountCircle sx={{ mr: 1 }} />
              {userData.full_name || 'Profile'}
            </MenuItem>
            <MenuItem 
              onClick={() => { 
                navigate('/profile'); 
                handleMenuClose(); 
              }}
              sx={{ 
                fontSize: '0.8rem', 
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'transparent',
                  cursor: 'default'
                }
              }}
            >
              {user?.email}
            </MenuItem>
            <MenuItem onClick={() => { navigate('/dashboard'); handleMenuClose(); }}>
              <Dashboard sx={{ mr: 1 }} />
              Dashboard
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          component={Link}
          to="/login"
          color="inherit"
          variant="outlined"
          sx={{
            borderColor: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          Login
        </Button>
      </Box>
    );
  };

  const renderMobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={toggleMobileDrawer}
    >
      <Box sx={{ width: 250, pt: 2 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem
              key={item.text}
              button
              component={Link}
              to={item.path}
              onClick={toggleMobileDrawer}
              sx={{
                backgroundColor: isActive(item.path) ? 'primary.light' : 'transparent',
                color: isActive(item.path) ? 'white' : 'inherit',
              }}
            >
              {item.icon}
              <ListItemText primary={item.text} sx={{ ml: 2 }} />
            </ListItem>
          ))}
          
          {isAuthenticated && authenticatedMenuItems.map((item) => (
            <ListItem
              key={item.text}
              button
              component={Link}
              to={item.path}
              onClick={toggleMobileDrawer}
              sx={{
                backgroundColor: isActive(item.path) ? 'primary.light' : 'transparent',
                color: isActive(item.path) ? 'white' : 'inherit',
              }}
            >
              {item.icon}
              <ListItemText primary={item.text} sx={{ ml: 2 }} />
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: 'primary.main',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleMobileDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: isMobile ? 1 : 0,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 700,
              fontSize: '1.5rem',
              mr: 4
            }}
          >
            Lewmax Agencies
          </Typography>
          
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1 }} />
              {renderDesktopMenu()}
              <Box sx={{ ml: 2 }}>
                {renderAuthButtons()}
              </Box>
            </>
          )}
          
          {isMobile && (
            <Box sx={{ ml: 'auto' }}>
              {renderAuthButtons()}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      {isMobile && renderMobileDrawer()}
    </>
  );
};

export default Navbar;
